import cn from "classnames"
import React from "react"
import { useTranslation } from "react-i18next"
import { connect } from "react-redux"
import ClientData from "@/common/client_data"
import Sign from "@/signomatic/app/sign"
import { currentPresetSettings } from "@/signomatic/app/sign_validator"
import CustomColorPicker from "@/signomatic/components/color_pickers/custom_color_picker"
import FixedColorPicker from "@/signomatic/components/color_pickers/fixed_color_picker"
import MultiColorPicker from "@/signomatic/components/color_pickers/multi_color_picker"
import { actions } from "@/signomatic/store/actions"
import { type State } from "@/signomatic/store/reducers"
import ColorUtil from "@/signomatic/util/colors"

type ColorSet = [string, string]

interface ColorToolboxOwnProps {
  sign: Sign
  custom?: boolean
}

interface ColorToolboxStateProps {
  data: SignData | null
  signState: SignState | null
  signType: string
  subToolbox: string | null
  subToolboxTarget: string | null
  activeObject: SignObjectState | null
}

interface ColorToolboxDispatchProps {
  addSubToolbox: typeof actions.addSubToolbox
}

type ColorToolboxProps = ColorToolboxStateProps & ColorToolboxDispatchProps & ColorToolboxOwnProps

const ColorToolbox: React.FC<ColorToolboxProps> = ({
  sign,
  signType,
  signState,
  data,
  activeObject,
  custom,
  addSubToolbox,
  subToolbox,
  subToolboxTarget,
}) => {
  const { t } = useTranslation()

  if (!data || !signState) {
    return null
  }

  let current: string | undefined = signState.color
  const objectBorder = ["object-border", "object-fill"].includes(subToolboxTarget || "")

  if (subToolboxTarget === "border") {
    current = signState.borderColor
  }

  if (activeObject) {
    if (subToolboxTarget === "object-border") {
      current = activeObject.border?.borderColor
    } else if (subToolboxTarget === "object-fill") {
      current = activeObject.border?.color
    } else if (subToolboxTarget === "object") {
      current = activeObject.color
    }
  }

  const updateColor = (color: string | undefined, picker = false) => {
    if (subToolboxTarget === "border") {
      sign.updateState({ borderColor: color })
    } else if (subToolboxTarget === "object-border") {
      sign.updateObjectBorder({ borderColor: color })
    } else if (subToolboxTarget === "object-fill") {
      sign.updateObjectBorder({ color })
    } else if (subToolboxTarget === "object") {
      if ((activeObject && Sign.isText(activeObject)) || !picker) {
        sign.updateObjectState({ color })
      }
    } else {
      sign.updateState({ color })
    }
  }

  const updateColorComplete = (color: string) => {
    if (subToolboxTarget === "object") {
      if (activeObject && Sign.isImage(activeObject)) {
        sign.updateObjectState({ color })
      }
    }
  }

  let customColorPicker = null

  if (custom) {
    if (current === null && objectBorder) {
      current = "#000000"
    }

    customColorPicker = (
      <CustomColorPicker
        key="customcolor"
        current={current}
        onChange={color => updateColor(ColorUtil.filterCustomColor(color, signType, signState), true)}
        onChangeComplete={color => {
          updateColorComplete(ColorUtil.filterCustomColor(color, signType, signState))
        }}
      />
    )
  }

  const adminColors = ClientData.admin ? data.admin_colors : []

  let disabledColors
  if (data.suggested_thickness && signState.thickness === 0.8) {
    disabledColors = data.fixed_colors.slice(0)

    if (
      disabledColors[0] &&
      disabledColors[3] &&
      disabledColors[0][0] === "#ffffff" &&
      disabledColors[0][1] === "#000000" &&
      disabledColors[3][0] === "#141414" &&
      disabledColors[3][1] === "#ffffff"
    ) {
      disabledColors.shift()
      disabledColors.splice(2, 1)
    }

    disabledColors = [...disabledColors, ...adminColors]
  }
  if (signType === "thermal_transfer_label" && signState.width === 30) {
    const sizeRestrictedColors = [
      ["#007cb0", "#000000"],
      ["#9b2423", "#000000"],
    ]
    disabledColors = [...(disabledColors || []), ...sizeRestrictedColors]
  }
  let suggestedColors = [...data.suggested_colors] as string[]
  if (subToolboxTarget) {
    suggestedColors = ColorUtil.filterSuggestedContentColors(suggestedColors, data)
  }

  let patternButton = null
  if (!subToolboxTarget && data.patterns.some(pattern => pattern.enabled)) {
    patternButton = (
      <div
        className={cn("color-button", "patterns", { active: subToolbox === "patterns" || signState.pattern })}
        onClick={() => addSubToolbox("patterns")}
      >
        <div className="color-wrapper">
          <div className="color-background"></div>
          <div className="color patterns" />
        </div>
        <div className="name">
          <p className="paragraph-2xs center mb-0">{t(`toolbox.patterns`)}</p>
        </div>
      </div>
    )

    if (signState.pattern) {
      current = undefined
    }
  }

  const presetMatch = currentPresetSettings(signState, data)

  if (data.transparent) {
    return (
      <FixedColorPicker<string>
        key="color"
        customPicker={customColorPicker}
        colors={data.fixed_colors as string[]}
        current={signState.contentColor}
        onChange={contentColor => {
          sign.updateState({ contentColor })
        }}
      />
    )
  } else if (data.dualcolor) {
    return (
      <FixedColorPicker<ColorSet>
        key="color"
        insideNote={["plastic_sign", "label_plates"].includes(signType)}
        customPicker={customColorPicker}
        colors={data.fixed_colors as ColorSet[]}
        adminColors={adminColors as ColorSet[]}
        disabledColors={disabledColors as ColorSet[]}
        current={[signState.color, signState.contentColor || ""]}
        onChange={set => {
          sign.updateState({ color: set[0], contentColor: set[1] })
        }}
        signType={signType}
      />
    )
  } else if (
    data.multicolor &&
    (!presetMatch ||
      (presetMatch &&
        data.multicolor &&
        (!presetMatch.color ||
          (subToolboxTarget && (subToolboxTarget.startsWith("object") || subToolboxTarget.startsWith("border"))))))
  ) {
    return (
      <MultiColorPicker
        key="color"
        colors={suggestedColors}
        current={current}
        showNone={objectBorder}
        patternButton={patternButton}
        customPicker={customColorPicker}
        onChange={color => {
          updateColor(color)
        }}
        onCustom={e => {
          e.stopPropagation()
          e.preventDefault()

          addSubToolbox("customcolor", subToolboxTarget)
        }}
      />
    )
  } else if (presetMatch && presetMatch.color) {
    return (
      <FixedColorPicker<string>
        key="color"
        colors={[presetMatch.color]}
        current={presetMatch.color}
        customPicker={customColorPicker}
        onChange={() => null}
      />
    )
  } else {
    return null
  }
}

export default connect(
  ({
    signType,
    signState,
    currentSignData,
    activeObject,
    subToolbox,
    subToolboxTarget,
  }: State): ColorToolboxStateProps => ({
    signType,
    signState,
    data: currentSignData,
    activeObject,
    subToolbox,
    subToolboxTarget,
  }),
  {
    addSubToolbox: actions.addSubToolbox,
  }
)(ColorToolbox)
